import BTThemeProvider from "@common-components/bt-theme-provider";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { Stack } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "@common/reducers/hooks/store.hook";
import { setRecurrenceText } from "@common/reducers/ScheduledMessageEditorReducer";
import { getUserStartDayOfWeek } from "@common/reducers/UserSelectors";
import { useIsMobile } from "@common/hooks/use-is-mobile";
import { scheduleSettingsGridSx } from "./schedule-settings-layout";
import MessageDeliveryOptions from "./message-delivery-options";
import MessageRecurrenceOptions from "./message-recurrence-options";
import { generateRecurrenceText, getRuleOptionsSafe, WeekStartDay } from "./recurrence-utils";
import ScheduleDatePicker from "./schedule-date-picker";
import ScheduleTimePicker from "./schedule-time-picker";
import { useTranslation } from 'react-i18next';
import pickerStyles from "./schedule-picker.module.scss";
import { namespace } from '@common/config/constants';
import { useClockPreference } from '@common/hooks/use-clock-preference';

type ScheduleSettingsProps = {
  showDeliveryOptions?: boolean;
  /** FE#240: restrict the recurrence frequency dropdown (default: all). */
  allowedFrequencies?: import('rrule').Frequency[];
};

export const ScheduleSettings = ({ showDeliveryOptions = true, allowedFrequencies }: ScheduleSettingsProps = {}) => {
  const { t, i18n } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  
  // Simplified state selectors
  const {
    dueDate,
    rruleset,
    isRecurring,
    cancelIfReceived,
    showRecurrence
  } = useAppSelector((state: any) => state.ScheduledMessageEditorReducer);
  const startDayAtWeek = useAppSelector(getUserStartDayOfWeek);
  const clockPreference = useClockPreference();
  const isMobile = useIsMobile();

  // Combined memoization for rule options and recurrence text
  const { ruleOptions, recurrenceText } = useMemo(() => {
    const options = getRuleOptionsSafe(rruleset, dueDate);
    const text = generateRecurrenceText(
      { dueDate, isRecurring, rruleset, cancelIfReceived },
      options,
      startDayAtWeek as WeekStartDay,
      t as (key: string, options?: any) => string,
      // Pass the render's language rather than leaning on moment's global
      // locale, which is set from an i18next event that can land after this.
      i18n.language,
      clockPreference,
    );
    return { ruleOptions: options, recurrenceText: text };
  }, [dueDate, isRecurring, rruleset, cancelIfReceived, startDayAtWeek, t, i18n.language, clockPreference]);

  // Combined effects
  useEffect(() => {
    dispatch(setRecurrenceText(recurrenceText));
    moment.updateLocale('en', { week: { dow: startDayAtWeek } });
  }, [recurrenceText, startDayAtWeek, dispatch]);


  return (
    <BTThemeProvider>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Stack
          direction="row"
          className={pickerStyles.picker}
          sx={{
            // Mobile restacks (date+time 2-up, recurrence+delivery full-width) so the
            // MUI date/time fields aren't crammed to ~78px; desktop keeps the roomy
            // single-row grid. scheduleSettingsGridSx is a pure, unit-tested helper.
            ...scheduleSettingsGridSx(isMobile),
            justifyContent: 'start', // Pack items to the start
            width: "100%",
            alignItems: 'center',
            gap: isMobile ? '10px' : 2,
            padding: '8px 0',
            '& > *': {
                width: isMobile ? '100% !important' : 'auto !important',
                minWidth: 0,
                maxWidth: '100%'
            }
          }}
        >
          <ScheduleDatePicker />
          <ScheduleTimePicker />
          {showRecurrence && <MessageRecurrenceOptions allowedFrequencies={allowedFrequencies} />}
          {showDeliveryOptions && <MessageDeliveryOptions />}
        </Stack>
      </LocalizationProvider>
    </BTThemeProvider>
  );
};

export default ScheduleSettings;
